// Australian formats through Intl: en-AU, Australia/Sydney by default (TECHSTACK.md, architecture Section 11).
export const LOCALE = 'en-AU';
export const DEFAULT_TIME_ZONE = 'Australia/Sydney';

const dateTime = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DEFAULT_TIME_ZONE,
  dateStyle: 'medium',
  timeStyle: 'short',
});
const dateOnly = new Intl.DateTimeFormat(LOCALE, { timeZone: DEFAULT_TIME_ZONE, dateStyle: 'medium' });
const timeOnly = new Intl.DateTimeFormat(LOCALE, { timeZone: DEFAULT_TIME_ZONE, timeStyle: 'short' });
const shortDateTime = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DEFAULT_TIME_ZONE,
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});
const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
const numberFormat = new Intl.NumberFormat(LOCALE);

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/** "2 Oct 2026, 10:02 am" */
export function formatDateTime(iso: string | null | undefined): string {
  return iso ? dateTime.format(new Date(iso)) : '';
}

export function formatDate(iso: string | null | undefined): string {
  return iso ? dateOnly.format(new Date(iso)) : '';
}

export function formatTime(iso: string | null | undefined): string {
  return iso ? timeOnly.format(new Date(iso)) : '';
}

/** "5 minutes ago", "yesterday", "3 weeks ago". */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  if (abs < MINUTE) {
    return relative.format(Math.round(diff / SECOND), 'second');
  }
  if (abs < HOUR) {
    return relative.format(Math.round(diff / MINUTE), 'minute');
  }
  if (abs < DAY) {
    return relative.format(Math.round(diff / HOUR), 'hour');
  }
  if (abs < WEEK) {
    return relative.format(Math.round(diff / DAY), 'day');
  }
  return relative.format(Math.round(diff / WEEK), 'week');
}

/** "1 h 20 min" for how long something has been waiting. */
export function formatDuration(fromIso: string, now: number = Date.now()): string {
  const ms = Math.max(0, now - new Date(fromIso).getTime());
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const minutes = Math.floor((ms % HOUR) / MINUTE);
  if (days > 0) {
    return `${days} d ${hours} h`;
  }
  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  return `${minutes} min`;
}

/** "26 Sept, 9:30 am" for timelines. */
export function formatShortDateTime(iso: string | null | undefined): string {
  return iso ? shortDateTime.format(new Date(iso)) : '';
}

export function formatNumber(value: number): string {
  return numberFormat.format(value);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) {
    return '';
  }
  const KB = 1024;
  if (bytes < KB) {
    return `${bytes} B`;
  }
  if (bytes < KB * KB) {
    return `${Math.round(bytes / KB)} KB`;
  }
  return `${(bytes / (KB * KB)).toFixed(1)} MB`;
}

/** "awaiting_reporter" -> "Awaiting reporter" for codes without a label from the API. */
export function humanise(code: string): string {
  const text = code.replaceAll('_', ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
